import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Lock, CreditCard, Loader2, AlertTriangle, Users, Mic, Minus, ChevronDown, ChevronUp, Send, CheckCircle, Sparkles } from "lucide-react";
import AddSeatButton from "./AddSeatButton";

function Meter({ icon: Icon, label, used, limit, extra }) {
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  const near = limit && used >= limit;
  return (
    <div className="flex-1 min-w-[150px]">
      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="w-3 h-3" />{label}</span>
        <span className={`font-mono font-semibold ${near && !extra ? "text-[#E11D48]" : "text-foreground"}`}>
          {used}{limit != null ? ` / ${limit}` : ""}{extra ? <span className="text-[#0025D8]"> {extra}</span> : null}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden">
        <div className={`h-full rounded-full ${near && !extra ? "bg-[#E11D48]" : "bg-[#0025D8]"}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const dollars = (cents) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

export default function CompanyBillingBanner() {
  const { user, checkAuth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paying, setPaying] = useState(false);
  const [seatBusy, setSeatBusy] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [packOpen, setPackOpen] = useState(false);
  const verifiedRef = useRef(false);
  const topupVerifiedRef = useRef(false);

  // Activation request form state
  const [formOpen, setFormOpen] = useState(false);
  const [formSent, setFormSent] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", company_name: "", message: "" });

  const billing = user?.company_billing;
  const isAdmin = user?.role === "company";

  // If /auth/me hasn't populated company_billing yet (e.g. right after signup), refresh once.
  useEffect(() => {
    if (user && (user.role === "company" || user.role === "company_employee") && !user.company_billing) {
      checkAuth();
    }
  }, [user, checkAuth]);

  // Handle the Stripe activation redirect: ?activated=1&session_id=...
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("activated") && sessionId && !verifiedRef.current) {
      verifiedRef.current = true;
      api.post("/company/activate-verify", { session_id: sessionId })
        .then(async () => { await checkAuth(); toast.success("Your plan is active — welcome aboard!"); })
        .catch(() => toast.error("We couldn't confirm your payment yet. It may take a moment to update."))
        .finally(() => { searchParams.delete("activated"); searchParams.delete("session_id"); setSearchParams(searchParams, { replace: true }); });
    }
    // Top-up redirect: ?topup=N&session_id=...
    if (searchParams.get("topup") && sessionId && !topupVerifiedRef.current) {
      topupVerifiedRef.current = true;
      api.post("/company/addons/interviews/verify", { session_id: sessionId })
        .then(async ({ data }) => { await checkAuth(); toast.success(`Added ${searchParams.get("topup")} interview credits`); })
        .catch(() => toast.error("We couldn't confirm your top-up yet. It may take a moment to update."))
        .finally(() => { searchParams.delete("topup"); searchParams.delete("session_id"); setSearchParams(searchParams, { replace: true }); });
    }
    if (searchParams.get("activation_cancelled") || searchParams.get("topup_cancelled")) {
      searchParams.delete("activation_cancelled"); searchParams.delete("topup_cancelled");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, checkAuth]);

  // Pre-fill form from user profile when available
  useEffect(() => {
    if (user && !formData.name) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || user.name || "",
        company_name: prev.company_name || user.name || "",
      }));
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitActivationRequest = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim() || !formData.company_name.trim()) {
      toast.error("Please fill in Name, Phone and Company name.");
      return;
    }
    setFormBusy(true);
    try {
      await api.post("/company/request-activation", formData);
      setFormSent(true);
      setFormOpen(false);
      toast.success("Your activation request has been sent — we'll be in touch soon.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request. Please try again.");
    } finally {
      setFormBusy(false);
    }
  };

  const startCheckout = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/company/activate-checkout", {});
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPaying(false);
    }
  };

  const removeSeat = async () => {
    if (!window.confirm("Remove one paid seat? You'll receive a prorated credit.")) return;
    setSeatBusy(true);
    try {
      await api.post("/company/addons/seats/remove", { quantity: 1 });
      await checkAuth();
      toast.success("Paid seat removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove seat");
    } finally { setSeatBusy(false); }
  };

  const buyPack = async (size) => {
    setPackBusy(true);
    try {
      const { data } = await api.post("/company/addons/interviews/checkout", { pack_size: size });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setPackBusy(false);
    }
  };

  if (!billing) return null;
  const {
    company_status: status, plan_label,
    effective_seat_limit, seats_used,
    interview_limit, interviews_used, interview_credits,
    addon_seats, interview_packs = [], addon_seat_price_cents, topup_price_cents,
    is_trial, trial_ends_at,
  } = billing;

  const trialDaysLeft = trial_ends_at
    ? Math.max(0, Math.ceil((new Date(trial_ends_at) - Date.now()) / 86400000))
    : null;

  // ── Active: usage panel + self-serve add-ons ──
  if (status === "active") {
    const hasLimits = effective_seat_limit != null || interview_limit != null;
    if (!hasLimits && !is_trial) return null;  // grandfathered/unlimited — nothing to show
    return (
      <div className="rounded-xl border border-border bg-white px-4 py-3.5 mb-6">
        {is_trial && (
          <div className="flex flex-wrap items-center gap-3 mb-3 pb-3 border-b border-border">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] font-bold text-[#7C3AED] bg-[#7C3AED]/10 px-2 py-1 rounded">
              <Sparkles className="w-3 h-3" /> Free trial
            </span>
            <span className="text-sm font-semibold text-foreground">
              {trialDaysLeft === 0
                ? "Ends today"
                : `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left`}
            </span>
            <span className="text-[11px] text-muted-foreground">
              Full access — no charge until you subscribe.
            </span>
            {isAdmin && (
              <button onClick={startCheckout} disabled={paying}
                className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-semibold rounded-lg transition-colors disabled:opacity-60">
                {paying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                Subscribe now
              </button>
            )}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{plan_label || "Plan"} usage</span>
          {effective_seat_limit != null && (
            <Meter icon={Users} label="Seats" used={seats_used} limit={effective_seat_limit}
              extra={addon_seats ? `(+${addon_seats} paid)` : null} />
          )}
          {interview_limit != null && (
            <Meter icon={Mic} label="Interviews this cycle" used={interviews_used} limit={interview_limit}
              extra={interview_credits ? `+${interview_credits} credits` : null} />
          )}
          {isAdmin && (
            <Link to="/company/billing" className="ml-auto text-[11px] font-semibold text-[#0025D8] hover:underline whitespace-nowrap">
              Billing &amp; invoices →
            </Link>
          )}
        </div>

        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border">
            <AddSeatButton
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/5 transition-colors disabled:opacity-60" />
            {addon_seats > 0 && (
              <button onClick={removeSeat} disabled={seatBusy} title="Remove a paid seat"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-muted-foreground hover:bg-secondary transition-colors disabled:opacity-60">
                <Minus className="w-3.5 h-3.5" /> Remove seat
              </button>
            )}
            <div className="relative">
              <button onClick={() => setPackOpen((v) => !v)} disabled={packBusy}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-[#0025D8]/30 text-[#0025D8] hover:bg-[#0025D8]/5 transition-colors disabled:opacity-60">
                {packBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
                Top up interviews
              </button>
              {packOpen && !packBusy && (
                <div className="absolute z-20 mt-1 left-0 bg-white border border-border rounded-xl shadow-lg p-1.5 min-w-[180px]">
                  <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Pick a pack</div>
                  {interview_packs.map((size) => (
                    <button key={size} onClick={() => { setPackOpen(false); buyPack(size); }}
                      className="w-full flex items-center justify-between px-2.5 py-2 text-sm rounded-lg hover:bg-secondary transition-colors">
                      <span className="font-medium">{size} interviews</span>
                      <span className="font-mono text-xs text-muted-foreground">{dollars(size * topup_price_cents)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Approved / past_due: activation payment CTA (admin only) ──
  if (status === "approved" || status === "past_due") {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-[#0025D8]/30 bg-[#0025D8]/5 px-4 py-3.5 mb-6">
        <CreditCard className="w-4 h-4 text-[#0025D8] flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0025D8]">
            {status === "past_due" ? "Your payment failed — reactivate to restore access" : `Activate your ${plan_label || ""} plan to get started`}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {isAdmin
              ? "Complete checkout to unlock roles, candidates and interviews for your team."
              : "Your company admin needs to complete payment before the account unlocks."}
          </p>
        </div>
        {isAdmin && (
          <button onClick={startCheckout} disabled={paying}
            className="flex items-center gap-2 px-4 py-2 bg-[#0025D8] hover:bg-[#001CB3] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 flex-shrink-0">
            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {status === "past_due" ? "Reactivate" : "Pay & activate"}
          </button>
        )}
      </div>
    );
  }

  // ── Canceled: subscription ended ──
  if (status === "canceled") {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5 mb-6">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">Your subscription has ended</p>
          <p className="text-xs text-amber-700 mt-0.5">Access is paused. Contact your administrator to reactivate your plan.</p>
        </div>
      </div>
    );
  }

  // ── Pending: activation request form (admin) or waiting message (member) ──
  if (formSent) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3.5 mb-6">
        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-800">Activation request sent</p>
          <p className="text-xs text-emerald-700 mt-0.5">We've received your request and will be in touch at {user?.email} soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50 mb-6 overflow-hidden">
      <div className="flex items-start gap-3 px-4 py-3.5">
        <Lock className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-800">Your account is awaiting activation</p>
          <p className="text-xs text-amber-700 mt-0.5">
            {isAdmin
              ? "Submit a request and our team will enable your account and assign a plan."
              : "Your company admin needs to request activation before the account unlocks. Please contact your administrator."}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setFormOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-amber-700 hover:bg-amber-800 text-white transition-colors flex-shrink-0"
          >
            {formOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {formOpen ? "Cancel" : "Request activation"}
          </button>
        )}
      </div>

      {formOpen && isAdmin && (
        <form onSubmit={submitActivationRequest} className="border-t border-amber-200 px-4 pb-4 pt-3 bg-white">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="Your name"
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                placeholder="+1 555 000 0000"
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Company name *</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => setFormData((p) => ({ ...p, company_name: e.target.value }))}
                placeholder="Your company"
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-muted-foreground mb-1">Brief message</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                placeholder="Tell us a bit about your hiring needs…"
                rows={3}
                className="w-full text-sm border border-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0025D8]/30 resize-none"
              />
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <p className="text-[11px] text-muted-foreground">We'll respond to {user?.email}</p>
            <button
              type="submit"
              disabled={formBusy}
              className="flex items-center gap-2 px-4 py-2 bg-[#0025D8] hover:bg-[#001CB3] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60"
            >
              {formBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send request
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
